// pages/profile/ManageGames.tsx
import { logger } from '../../utils/logger';
// 🆕 UPDATED: Emits profile update events to refresh ProfileContext cache

import React, { useState, useEffect, useCallback } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonSearchbar,
  IonList,
  IonItem,
  IonLabel,
  IonSpinner,
  IonButton,
  IonAlert,
  IonToast,
  IonSegment,
  IonSegmentButton,
  IonBadge,
  IonIcon,
} from '@ionic/react';
import { addCircleOutline } from 'ionicons/icons';
import { gameService } from '../../services/gameService';
import { UserGame, OwnershipStatus, TTRPGPreference } from '../../types/games';
import { GameCard } from '../../components/GameCard';
import { GamePreferencesModal } from '../../components/GamePreferencesModal';
import { CustomGameModal } from '../../components/CustomGameModal';
import { emitProfileUpdate } from '../../contexts/ProfileContext';
import './ManageGames.css';

type FilterType = 'all' | 'owned' | 'want' | 'rpg';

interface SearchableGame {
  id?: number | string;
  name: string;
  bgg_id?: number;
  game_type?: string;
  gameType?: string;
  year_published?: number;
  yearPublished?: number;
  min_players?: number;
  minPlayers?: number;
  max_players?: number;
  maxPlayers?: number;
  min_playtime?: number;
  max_playtime?: number;
  minPlaytime?: number;
  maxPlaytime?: number;
  playingTime?: number;
  short_name?: string;
  is_system?: boolean;
  image_url?: string;
}

const ManageGames: React.FC = () => {
  // Game data state
  const [userGames, setUserGames] = useState<UserGame[]>([]);
  const [searchResults, setSearchResults] = useState<SearchableGame[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Loading states
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [addingGame, setAddingGame] = useState(false);

  // Modal states
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [showCustomGameModal, setShowCustomGameModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<UserGame | null>(null);
  const [gameToDelete, setGameToDelete] = useState<string | null>(null);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Filter state
  const [filterSegment, setFilterSegment] = useState<FilterType>('all');

  // Load user's games on mount
  useEffect(() => {
    loadUserGames();
  }, []);

  /**
   * Load user's game collection from database
   */
  const loadUserGames = async () => {
    setLoading(true);
    try {
      const games = await gameService.getUserGames();
      setUserGames(games || []);
    } catch (error) {
      logger.error('Error loading games:', error);
      showToastMessage('Failed to load your games');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Search for games in the database
   */
  const handleSearch = async (query: string) => {
    setSearchQuery(query);

    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Search database for games (121K+ games)
      const results = await gameService.searchGames(query);
      setSearchResults(results);
    } catch (error) {
      logger.error('Search error:', error);
      showToastMessage('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };


  /**
   * Normalize game data from search results to consistent format
   */
  const normalizeGameData = (game: SearchableGame) => {
    return {
      game_id: game.bgg_id || game.id || 0,
      name: game.name,
      game_type: game.game_type || game.gameType || 'boardgame',
      min_players: game.min_players || game.minPlayers,
      max_players: game.max_players || game.maxPlayers,
      min_playtime: game.min_playtime || game.minPlaytime || game.playingTime,
      max_playtime: game.max_playtime || game.maxPlaytime || game.playingTime,
      year_published: game.year_published || game.yearPublished,
      short_name: game.short_name,
      is_system: game.is_system,
      image_url: game.image_url,
    };
  };

  /**
   * Add a game to user's collection
   */
  const handleAddGame = async (searchResult: SearchableGame) => {
    // Validate game data
    if (!searchResult.name || searchResult.name === 'Unknown Game') {
      showToastMessage('Invalid game name');
      return;
    }

    // Check if already in collection
    const alreadyOwned = userGames.some(g =>
      g.name.toLowerCase() === searchResult.name.toLowerCase()
    );

    if (alreadyOwned) {
      showToastMessage('You already have this game in your collection');
      return;
    }

    setAddingGame(true);
    logger.log('Adding game:', searchResult);

    try {
      const gameData = normalizeGameData(searchResult);
      const result = await gameService.addUserGame(gameData);

      logger.log('Add game result:', result);

      if (result.success && result.game) {
        // 🆕 Emit profile update event
        emitProfileUpdate();

        showToastMessage(`${searchResult.name} added to your collection!`);
        setSearchQuery('');
        setSearchResults([]);

        // Reload games and open preferences modal
        await loadUserGames();
        setSelectedGame(result.game);
        setShowPreferencesModal(true);
      } else {
        showToastMessage(result.error || 'Failed to add game');
      }
    } catch (error) {
      logger.error('Error adding game:', error);
      showToastMessage('An error occurred while adding the game');
    } finally {
      setAddingGame(false);
    }
  };

  /**
   * Add a custom game (user-entered)
   */
  const handleAddCustomGame = async (gameData: {
    name: string;
    game_type: 'boardgame' | 'rpg';
    min_players?: number;
    max_players?: number;
    year_published?: number;
  }) => {
    // Validate name
    if (!gameData.name.trim()) {
      showToastMessage('Please enter a game name');
      return;
    }

    // Check if already exists
    const alreadyOwned = userGames.some(g =>
      g.name.toLowerCase() === gameData.name.toLowerCase()
    );

    if (alreadyOwned) {
      showToastMessage('You already have this game in your collection');
      return;
    }

    try {
      const result = await gameService.addCustomGame(gameData);

      if (result.success && result.game) {
        // 🆕 Emit profile update event
        emitProfileUpdate();

        showToastMessage(`${gameData.name} added to your collection!`);
        setShowCustomGameModal(false);

        // Reload games and open preferences modal
        await loadUserGames();
        setSelectedGame(result.game);
        setShowPreferencesModal(true);
      } else {
        showToastMessage(result.error || 'Failed to add custom game');
      }
    } catch (error) {
      logger.error('Error adding custom game:', error);
      showToastMessage('An error occurred while adding the game');
    }
  };

  /**
   * Open edit modal for a game
   */
  const handleEditGame = useCallback((game: UserGame) => {
    setSelectedGame(game);
    setShowPreferencesModal(true);
  }, []);

  /**
   * Save game preferences
   */
  const handleSavePreferences = async (preferences: {
    ownership_status?: OwnershipStatus;
    ttrpg_preference?: TTRPGPreference;
  }) => {
    if (!selectedGame) return;

    try {
      const result = await gameService.updateUserGame(selectedGame.id, preferences);

      if (result.success) {
        // 🆕 Emit profile update event
        emitProfileUpdate();

        showToastMessage('Game preferences updated!');
        setShowPreferencesModal(false);
        setSelectedGame(null);
        await loadUserGames();
      } else {
        showToastMessage(result.error || 'Failed to update preferences');
      }
    } catch (error) {
      logger.error('Error updating preferences:', error);
      showToastMessage('An error occurred while updating preferences');
    }
  };

  /**
   * Confirm game deletion
   */
  const confirmDelete = useCallback((gameId: string) => {
    setGameToDelete(gameId);
    setShowDeleteAlert(true);
  }, []);

  /**
   * Delete a game from user's collection
   */
  const handleDeleteGame = async () => {
    if (!gameToDelete) return;

    try {
      const result = await gameService.removeUserGame(gameToDelete);

      if (result.success) {
        // 🆕 Emit profile update event
        emitProfileUpdate();

        showToastMessage('Game removed from your collection');
        await loadUserGames();
      } else {
        showToastMessage(result.error || 'Failed to remove game');
      }
    } catch (error) {
      logger.error('Error deleting game:', error);
      showToastMessage('An error occurred while removing the game');
    } finally {
      setGameToDelete(null);
    }
  };

  /**
   * Show a toast message
   */
  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  /**
   * Filter games based on selected segment
   */
  const getFilteredGames = (): UserGame[] => {
    switch (filterSegment) {
      case 'owned':
        return userGames.filter(g => g.ownership_status === 'own');
      case 'want':
        return userGames.filter(g => g.ownership_status === 'want-to-try');
      case 'rpg':
        return userGames.filter(g =>
          g.game_type?.toLowerCase().includes('rpg') ||
          g.ttrpg_preference === 'run' ||
          g.ttrpg_preference === 'both'
        );
      default:
        return userGames;
    }
  };

  // Calculate counts for filter badges
  const filteredGames = getFilteredGames();
  const ownedCount = userGames.filter(g => g.ownership_status === 'own').length;
  const wantCount = userGames.filter(g => g.ownership_status === 'want-to-try').length;
  const rpgCount = userGames.filter(g =>
    g.game_type?.toLowerCase().includes('rpg') ||
    g.ttrpg_preference === 'run' ||
    g.ttrpg_preference === 'both'
  ).length;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/profile" />
          </IonButtons>
          <IonTitle>My Games</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setShowCustomGameModal(true)}>
              <IonIcon slot="icon-only" icon={addCircleOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Search Section */}
        <div className="search-section">
          <IonSearchbar
            value={searchQuery}
            onIonInput={(e) => handleSearch(e.detail.value!)}
            placeholder="Search board games & RPGs..."
            debounce={500}
            disabled={addingGame}
          />

          {searching && (
            <div className="search-loading">
              <IonSpinner />
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && !searching && (
            <IonList className="search-results">
              {searchResults.map((game, index) => (
                <IonItem
                  key={`${game.id || game.bgg_id}-${index}`}
                  button
                  onClick={() => handleAddGame(game)}
                  disabled={addingGame}
                >
                  <IonLabel>
                    <h3>{game.name}</h3>
                    {(game.year_published || game.yearPublished) && (
                      <p>({game.year_published || game.yearPublished})</p>
                    )}
                    {((game.min_players || game.minPlayers) || (game.max_players || game.maxPlayers)) && (
                      <p>
                        {game.min_players || game.minPlayers}-{game.max_players || game.maxPlayers} players
                        {(game.playingTime || game.min_playtime) &&
                          ` • ${game.playingTime || game.min_playtime} min`}
                      </p>
                    )}
                  </IonLabel>
                  {addingGame && <IonSpinner slot="end" />}
                </IonItem>
              ))}
            </IonList>
          )}
        </div>

        {/* Filter Tabs */}
        {userGames.length > 0 && (
          <div className="filter-segment-container">
            <IonSegment
              value={filterSegment}
              onIonChange={(e) => setFilterSegment(e.detail.value as FilterType)}
            >
              <IonSegmentButton value="all">
                <IonLabel>All ({userGames.length})</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="owned">
                <IonLabel>
                  Owned
                  {ownedCount > 0 && (
                    <IonBadge color="success">{ownedCount}</IonBadge>
                  )}
                </IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="want">
                <IonLabel>
                  Want
                  {wantCount > 0 && (
                    <IonBadge color="warning">{wantCount}</IonBadge>
                  )}
                </IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="rpg">
                <IonLabel>
                  RPGs
                  {rpgCount > 0 && (
                    <IonBadge color="tertiary">{rpgCount}</IonBadge>
                  )}
                </IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </div>
        )}

        {/* Games List */}
        <div className="user-games-section">
          {loading ? (
            <div className="loading-container">
              <IonSpinner />
              <p>Loading your games...</p>
            </div>
          ) : filteredGames.length === 0 ? (
            <div className="empty-state">
              <p>
                {filterSegment === 'all'
                  ? 'No games in your collection yet. Search above to add games!'
                  : `No games in the "${filterSegment}" category.`}
              </p>
              {filterSegment !== 'all' && (
                <IonButton
                  fill="clear"
                  onClick={() => setFilterSegment('all')}
                >
                  View All Games
                </IonButton>
              )}
            </div>
          ) : (
            <div className="games-list">
              {filteredGames.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  onEdit={handleEditGame}
                  onDelete={confirmDelete}
                />
              ))}
            </div>
          )}
        </div>

        {/* Modals */}
        <GamePreferencesModal
          isOpen={showPreferencesModal}
          game={selectedGame}
          onDismiss={() => {
            setShowPreferencesModal(false);
            setSelectedGame(null);
          }}
          onSave={handleSavePreferences}
        />

        <CustomGameModal
          isOpen={showCustomGameModal}
          onDismiss={() => setShowCustomGameModal(false)}
          onSave={handleAddCustomGame}
        />

        {/* Delete Confirmation Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="Remove Game"
          message="Are you sure you want to remove this game from your collection?"
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Remove',
              role: 'destructive',
              handler: handleDeleteGame,
            },
          ]}
        />

        {/* Toast Notifications */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          position="bottom"
          aria-live="polite"
        />
      </IonContent>
    </IonPage>
  );
};

export default ManageGames;